// React / Next
import { useEffect, useRef, useState, useCallback } from 'react';

// Styles
import styles from './MysticMirrorVector.module.css';

// Types
interface MysticMirrorVectorProps {
  className?: string;
  width?: number;
  height?: number;
}

// Animation duration in ms (base duration + max delay for all paths)
const ANIMATION_DURATION = 800 + 20 * 30; // 0.8s base + ~20 unique distances * 0.03s delay

/**
 * MysticMirrorVector - Animated SVG with mystic portal effect on hover
 * Paths animate from edges to center with scaleX compression effect
 */
export const MysticMirrorVector = ({
  className = '',
  width = 493,
  height = 455,
}: MysticMirrorVectorProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const paths = Array.from(svg.querySelectorAll('g path')) as SVGPathElement[];

    // Calculate center of SVG
    const svgWidth = 493;
    const centerX = svgWidth / 2;

    // Calculate distance from center for each path
    const items = paths.map((path) => {
      const box = path.getBBox();
      const itemCenterX = box.x + box.width / 2;
      const distance = Math.abs(itemCenterX - centerX);
      return { element: path, dist: distance };
    });

    // Get unique distances sorted from farthest to nearest (edges animate first)
    const uniqueDistances = [...new Set(items.map((i) => Math.round(i.dist)))].sort(
      (a, b) => b - a
    );

    // Assign distance index based on distance from center
    items.forEach((item) => {
      const distIndex = uniqueDistances.indexOf(Math.round(item.dist));
      item.element.style.setProperty('--dist-i', String(distIndex));
    });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  }, [isAnimating]);

  return (
    <svg
      ref={svgRef}
      className={`${styles.svg} ${isAnimating ? styles.animating : ''} ${className}`}
      width={width}
      height={height}
      viewBox="0 0 493 455"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Mystic mirror animated vector"
      role="img"
      onMouseEnter={handleMouseEnter}
    >
      <rect width="493" height="455" fill="#6AEAFD" />
      <g clipPath="url(#mysticMirrorClip)">
        <path d="M266.241 0.015625H227.036V454.984H266.241V0.015625Z" fill="#022E40" />
        <path d="M89.3162 169.954H85.9531V286.523H89.3162V169.954Z" fill="#022E40" />
        <path d="M129.896 55.8425H122.929V400.549H129.896V55.8425Z" fill="#022E40" />
        <path d="M127.406 0.015625H125.399V454.984H127.406V0.015625Z" fill="#022E40" />
        <path d="M138.648 169.954H114.158V286.523H138.648V169.954Z" fill="#022E40" />
        <path d="M184.3 55.8425H150.465V400.549H184.3V55.8425Z" fill="#022E40" />
        <path d="M175.104 0.015625H158.325V454.984H175.104V0.015625Z" fill="#022E40" />
        <path d="M230.641 55.8425H196.806V400.549H230.641V55.8425Z" fill="#022E40" />
        <path d="M224.119 0.015625H188.537V454.984H224.119V0.015625Z" fill="#022E40" />
        <path d="M194.408 169.954H147.083V286.523H194.408V169.954Z" fill="#022E40" />
        <path d="M405.931 286.523H409.294V169.954H405.931V286.523Z" fill="#022E40" />
        <path d="M365.351 400.549H372.318L372.318 55.8425H365.351L365.351 400.549Z" fill="#022E40" />
        <path d="M367.84 454.984H369.847L369.847 0.0158081H367.84L367.84 454.984Z" fill="#022E40" />
        <path d="M356.599 286.523H381.088V169.954H356.599V286.523Z" fill="#022E40" />
        <path d="M310.946 400.549H344.781L344.781 55.8425H310.946L310.946 400.549Z" fill="#022E40" />
        <path d="M320.162 454.984H336.94L336.94 0.0158081H320.162L320.162 454.984Z" fill="#022E40" />
        <path d="M264.606 400.549H298.441L298.441 55.8425H264.606L264.606 400.549Z" fill="#022E40" />
        <path d="M271.128 454.984H306.71L306.71 0.0158081H271.128L271.128 454.984Z" fill="#022E40" />
        <path d="M300.857 286.523H348.182V169.954H300.857V286.523Z" fill="#022E40" />
      </g>
      <defs>
        <clipPath id="mysticMirrorClip">
          <rect
            width="323.34"
            height="454.968"
            fill="white"
            transform="translate(85.9531 0.015625)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

